/**
 * Interaction States Design System Tokens
 * 
 * SOURCE OF TRUTH for hover, active, and selected states
 * across /admin and /account areas.
 */

export const INTERACTION_STATES = {
  // ─────────────────────────────────────────────────────────────────
  // LIST ITEM INTERACTIONS
  // ─────────────────────────────────────────────────────────────────
  listItem: {
    hover: "hover:bg-muted/50 transition-colors",
    selected: "bg-muted/30",
    hoverSelected: "hover:bg-muted/50 bg-muted/30 transition-colors",
    active: "bg-primary/5 border-primary",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // TABLE ROW INTERACTIONS
  // ─────────────────────────────────────────────────────────────────
  tableRow: {
    hover: "hover:bg-muted/30",
    clickable: "cursor-pointer hover:bg-muted/30 transition-colors",
    selected: "bg-muted/20",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // DESTRUCTIVE ACTION BUTTONS
  // Used for delete/remove icon buttons that need subtle destructive hint
  // ─────────────────────────────────────────────────────────────────
  destructiveGhost: {
    default: "text-muted-foreground hover:text-destructive hover:bg-destructive/10",
    active: "text-destructive hover:text-destructive",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // BADGE/CHIP INTERACTIONS
  // For interactive badges that respond to hover
  // ─────────────────────────────────────────────────────────────────
  badge: {
    hover: "cursor-pointer hover:bg-primary/10 transition-colors",
    hoverWithBorder: "cursor-pointer hover:bg-primary/10 hover:border-primary/30 transition-colors",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // REMOVE/CLOSE BUTTON (inline × buttons)
  // ─────────────────────────────────────────────────────────────────
  removeButton: {
    default: "cursor-pointer hover:text-destructive",
    withBackground: "hover:bg-destructive hover:text-destructive-foreground",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // COLLAPSIBLE TRIGGERS
  // ─────────────────────────────────────────────────────────────────
  collapsible: {
    trigger: "cursor-pointer hover:bg-muted/50 transition-colors",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // CARD INTERACTIONS
  // ─────────────────────────────────────────────────────────────────
  card: {
    hover: "hover:bg-muted/50 transition-colors",
    clickable: "cursor-pointer hover:bg-muted/50 transition-colors",
    selected: "border-primary bg-primary/5",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // NAVIGATION BUTTON INTERACTIONS (Header-style triggers)
  // SOURCE OF TRUTH for all header navigation buttons and selectors
  // Pattern from: Header.tsx navigation buttons
  // ─────────────────────────────────────────────────────────────────
  navButton: {
    /** Standard navigation button hover - use for header nav buttons, dropdowns */
    hover: "hover:bg-muted hover:text-foreground transition-colors",
    /** Focus state matching navigation style */
    focus: "focus:bg-muted focus:text-foreground focus:outline-none",
    /** Combined hover + focus for interactive buttons (header dropdowns, selectors) */
    interactive: "hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground focus:outline-none transition-colors",
    /** Active/open state */
    active: "bg-muted/50",
  },
} as const;

export type InteractionStatesType = typeof INTERACTION_STATES;
